import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { X, CheckCircle, Briefcase, ChevronRight, Sparkles, ShieldCheck } from 'lucide-react';
import { PractitionerForm } from './Forms/PractitionerForm';
import { EventForm } from './Forms/EventForm';
import { VenueForm } from './Forms/VenueForm';
import { JobForm } from './Forms/JobForm';
import { isProfessional } from '../../lib/auth/authService';
import { BottomSheet } from '../ui/BottomSheet';

const LISTING_TYPES = [
  {
    id: 'practitioner',
    labelKey: 'listing.practitioner',
    descKey: 'listing.practitionerDesc',
    iconUrl: 'https://afvltpqnhmaxanirwnqz.supabase.co/storage/v1/object/public/icons/pracitioner-icon.png',
    helper: 'Best for coaches, therapists and holistic professionals.',
    extra: 'Profile, services, location and credentials',
    featured: true
  },
  {
    id: 'event',
    labelKey: 'listing.eventOrRetreat',
    descKey: 'listing.eventOrRetreatDesc',
    iconUrl: 'https://afvltpqnhmaxanirwnqz.supabase.co/storage/v1/object/public/icons/calendar-icon.png',
    helper: 'Perfect for workshops, retreats and classes.',
    extra: 'Schedule, tickets and organiser details'
  },
  {
    id: 'venue',
    labelKey: 'listing.venueOrSpace',
    descKey: 'listing.venueOrSpaceDesc',
    iconUrl: 'https://afvltpqnhmaxanirwnqz.supabase.co/storage/v1/object/public/icons/venue-icon.png',
    helper: 'List studios, centres and mindful spaces.',
    extra: 'Capacity, amenities and rental information'
  },
  {
    id: 'job',
    labelKey: 'listing.jobOpportunity',
    descKey: 'listing.jobOpportunityDesc',
    iconUrl: 'https://afvltpqnhmaxanirwnqz.supabase.co/storage/v1/object/public/icons/handshake.png',
    helper: 'Share open roles in the wellness ecosystem.',
    extra: 'Job details, salary range and contact info'
  },
];

interface AddListingModalProps {
  onClose: () => void;
  editId?: string;
  editType?: string;
  onSuccess?: () => void;
}

export function AddListingModal({ onClose, editId, editType, onSuccess }: AddListingModalProps) {
  const { t } = useTranslation();
  const [selectedType, setSelectedType] = useState<string | null>(null);
  const [showSuccess, setShowSuccess] = useState(false);
  const [canCreateListing, setCanCreateListing] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // If editing, set the selected type
    if (editType) {
      setSelectedType(editType);
    }
    
    const checkAccess = async () => {
      const hasProfessionalAccess = await isProfessional();
      setCanCreateListing(hasProfessionalAccess);
      setLoading(false);
    };
    checkAccess();
  }, [editType]);

  if (loading) {
    return null;
  }

  if (!canCreateListing) {
    const content = (
      <div className="fixed inset-0 min-h-[100dvh] bg-black/50 flex items-center justify-center z-[9999]" onClick={onClose}>
        <div className="bg-white rounded-2xl p-8 max-w-md w-full mx-4 text-center shadow-xl" onClick={e => e.stopPropagation()}>
          <button onClick={onClose} className="absolute top-4 right-4 p-2 text-content/40 hover:text-content rounded-full">
            <X className="h-5 w-5" />
          </button>
          <div className="w-16 h-16 bg-accent-text/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <Briefcase className="w-8 h-8 text-accent-text" />
          </div>
          <h2 className="text-xl font-gelica font-bold mb-2">Professional membership required</h2>
          <p className="text-content/60 mb-6 text-sm">
            Creating listings is available to professional members. Upgrade your membership to start listing your practice, events, venues, and job opportunities.
          </p>
          <a
            href="/pricing"
            className="inline-flex items-center px-6 py-3 bg-accent-text text-white rounded-full font-medium hover:bg-accent-text/90 transition-colors"
          >
            View Pricing
            <ChevronRight className="ml-1 w-4 h-4" />
          </a>
        </div>
      </div>
    );
    return typeof document !== 'undefined' ? createPortal(content, document.body) : content;
  }

  const handleClose = () => {
    onClose();
    if (onSuccess) {
      onSuccess();
    }
  };

  if (showSuccess) {
    const content = (
      <div className="fixed inset-0 min-h-[100dvh] bg-black bg-opacity-50 flex items-center justify-center z-[9999]">
        <div className="bg-white rounded-lg p-8 max-w-md w-full mx-4 text-center">
          <div className="flex justify-center mb-6">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
              <CheckCircle className="h-8 w-8 text-green-600" />
            </div>
          </div>
          <h3 className="text-xl font-semibold text-gray-900 mb-2">
            {t('listing.submissionTitle')}
          </h3>
          <p className="text-gray-600 mb-6">
            {t('listing.submissionMessage')}
          </p>
          <button
            onClick={handleClose}
            className="w-full px-4 py-2 bg-accent-text text-white rounded-lg hover:bg-accent-text/90 transition-colors"
          >
            {t('common.close')}
          </button>
        </div>
      </div>
    );
    return typeof document !== 'undefined' ? createPortal(content, document.body) : content;
  }

  const handleSuccess = () => {
    setShowSuccess(true);
  };
  const isTypeSelectionStep = !selectedType;

  const renderForm = () => {
    switch (selectedType) {
      case 'practitioner':
        return <PractitionerForm onClose={onClose} onSuccess={handleSuccess} editId={editId} />;
      case 'event':
        return <EventForm onClose={onClose} onSuccess={handleSuccess} editId={editId} />;
      case 'venue':
        return <VenueForm onClose={onClose} onSuccess={handleSuccess} editId={editId} />;
      case 'job':
        return <JobForm onClose={onClose} onSuccess={handleSuccess} editId={editId} />;
      default:
        return null;
    }
  };

  if (isTypeSelectionStep) {
    return (
      <BottomSheet
        isOpen
        onClose={onClose}
        maxHeight={88}
        desktopMode="modal"
        desktopMaxWidthClass="max-w-4xl"
        panelClassName="bg-background rounded-t-3xl lg:rounded-2xl"
      >
        <div className="relative overflow-hidden border-b border-[#8DA847]/15 bg-gradient-to-br from-[#F8FAF5] via-white to-[#F3F7EE]">
          <div className="absolute -top-8 -right-10 h-32 w-32 rounded-full bg-[#8DA847]/10 blur-2xl" />
          <div className="absolute -bottom-10 -left-10 h-32 w-32 rounded-full bg-[#8DA847]/10 blur-2xl" />
          <div className="relative flex items-start justify-between p-5 sm:p-6">
            <div>
              <div className="inline-flex items-center gap-2 rounded-full border border-[#8DA847]/20 bg-white/90 px-3 py-1 text-xs font-semibold text-[#2F4F3E] mb-3">
                <Sparkles className="h-3.5 w-3.5 text-[#8DA847]" />
                Mindful Family Pro
              </div>
              <h2 className="text-xl sm:text-2xl font-gelica font-bold text-[#2F4F3E]">
                {t('listing.createNewListing')}
              </h2>
              <p className="text-content/60 text-sm mt-1.5 max-w-xl">
                {t('listing.whatToCreate')}
              </p>
            </div>
            <button onClick={onClose} className="text-content/40 hover:text-content/70 transition-colors p-2 -mr-2 rounded-lg hover:bg-white/80">
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        <div className="p-5 sm:p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3.5">
            {LISTING_TYPES.map((type) => {
              return (
                <button
                  key={type.id}
                  onClick={() => setSelectedType(type.id)}
                  className="group relative w-full text-left rounded-2xl border border-[#E6EDE0] bg-white p-4 sm:p-5 hover:border-[#8DA847]/45 hover:shadow-lg hover:shadow-[#8DA847]/10 active:scale-[0.99] transition-all duration-200"
                >
                  {type.featured && (
                    <span className="absolute right-3 top-3 inline-flex items-center rounded-full bg-[#C4714A]/10 px-2.5 py-1 text-[11px] font-semibold text-[#9A603E]">
                      Popular
                    </span>
                  )}
                  <div className="mb-4 inline-flex h-12 w-12 items-center justify-center rounded-xl bg-[#8DA847]/10">
                    <img
                      src={type.iconUrl}
                      alt={t(type.labelKey)}
                      className="h-7 w-7 object-contain"
                      loading="lazy"
                    />
                  </div>
                  <h3 className="text-base sm:text-lg font-semibold text-[#2F4F3E] mb-1">
                    {t(type.labelKey)}
                  </h3>
                  <p className="text-sm text-content/65 leading-relaxed mb-3">
                    {t(type.descKey)}
                  </p>
                  <p className="text-xs font-medium text-[#2F4F3E]/85 mb-2">
                    {type.helper}
                  </p>
                  <div className="inline-flex items-center rounded-full bg-[#F3F7EE] px-2.5 py-1 text-[11px] font-medium text-[#2F4F3E]/80">
                    {type.extra}
                  </div>
                  <div className="mt-4 flex items-center text-sm font-medium text-[#8DA847]">
                    Continue
                    <ChevronRight className="ml-1 h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                  </div>
                </button>
              );
            })}
          </div>

          <div className="mt-5 rounded-xl border border-[#8DA847]/20 bg-[#F8FAF5] px-4 py-3 text-xs text-[#2F4F3E]/80 flex items-start gap-2.5">
            <ShieldCheck className="h-4 w-4 mt-0.5 text-[#8DA847] flex-shrink-0" />
            All listings are reviewed before they go live to keep quality high and trusted for the community.
          </div>
        </div>
      </BottomSheet>
    );
  }

  // Listing forms already render their own fullscreen modal + backdrop.
  // Returning them directly avoids a second overlay layer.
  return renderForm();
}